/* global runtime */
// Virksomhetsprofil, MVA-betalinger og brukerprofil.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const saveBusinessProfile = async (profile = {}) => {
  const result = await authorizedPost('businessProfileMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'save_profile', profile,
  });
  applyVatMutationResult(result);
  return result;
};const saveAccountingStartupProfile=async(profile={})=>{const result=await authorizedPost('businessProfileMutationHttp',{businessId:runtime.activeBusinessId,action:'save_accounting_startup_profile',profile});applyVatMutationResult(result);return result;};
const saveOpeningBalance = async ({ year, openingBalance } = {}) => {
  const result = await authorizedPost('businessProfileMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'save_opening_balance', year, openingBalance,
  });
  applyVatMutationResult(result);
  return result;
};const completeOpeningBalance = async ({ year, idempotencyKey, confirmZeroBalance = false } = {}) => { const result = await authorizedPost('businessProfileMutationHttp', { businessId: runtime.activeBusinessId, action: 'complete_opening_balance', year, idempotencyKey, confirmZeroBalance }); applyVatMutationResult(result); return result; };
const analyzeOpeningBalanceImage=async(file)=>{if(!file||!/^image\/(?:png|jpeg|webp)$/i.test(file.type||''))throw new Error(t('set.transitionImport.imageType'));if(file.size>8*1024*1024)throw new Error(t('set.transitionImport.imageSize'));const base64=await new Promise((resolve,reject)=>{const reader=new FileReader();reader.onload=()=>resolve(String(reader.result||''));reader.onerror=()=>reject(reader.error||new Error(t('set.transitionImport.readError')));reader.readAsDataURL(file);});const result=await authorizedPost('analyzeOpeningBalanceImageHttp',{businessId:runtime.activeBusinessId,fileName:file.name||'inngaaende-balanse.png',mimeType:file.type,base64},{requestTimeoutMs:90000,timeoutMessage:t('set.transitionImport.imageTimeout')});return result.parsed;};const saveBankOpeningSetup = async ({ year, bankAccountId, startBalance, startBalanceDate, transitionDate, counterAccountCode, note } = {}) => {
  const result = await authorizedPost('businessProfileMutationHttp', {
    businessId: runtime.activeBusinessId,
    action: 'save_bank_opening_setup',
    year,
    bankAccountId,
    startBalance,
    startBalanceDate,
    transitionDate,
    counterAccountCode,
    note,
  });
  applyVatMutationResult(result);
  return result;
};
const saveCustomsDeclaration = async (entry) => {
  const result = await authorizedPost('vatMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'upsert_customs', entry,
  });
  applyVatMutationResult(result);
  return result.entry;
};

const deleteCustomsDeclaration = async (entryId) => {
  const result = await authorizedPost('vatMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'annul_customs', entryId,
  });
  applyVatMutationResult(result);
};
// Plumme cannot file VAT returns: the user files with Skatteetaten, then marks the term here (`filed: false` clears).
const markVatTermFiledOutside=async({year,termKey,filed=true,filedDate,reference,comment}={})=>{
  const result = await authorizedPost('vatMutationHttp', { businessId: runtime.activeBusinessId, action: filed ? 'mark_filed_outside' : 'clear_filed_outside', year, termKey, filedDate, reference, comment });
  return applyVatMutationResult(result)?.filing || null;
};
const getVatTermFiling=(x={})=>runtime.appState?.deadlinesByYear?.[x.year]?.mvaTermFilings?.[x.termKey]||null;
// Forsinkelsesrente og gebyr foelger betalingen ut av banken, men er ikke MVA.
// Speiler functions/accounting/vatMutationHttp.ts.
const vatSurchargeAmount = (record, key) =>
  record?.settlementKind === 'refund' ? 0 : Math.max(0, num(record?.[key]));
const vatLateInterestAmount = (record) => vatSurchargeAmount(record, 'lateInterestAmount');
const vatPenaltyFeeAmount = (record) => vatSurchargeAmount(record, 'penaltyFeeAmount');

const vatPaymentBankSignature = (record) => JSON.stringify({
  amount: Math.abs(num(record?.amount)),
  date: String(record?.date || '').slice(0, 10),
  settlementAccountCode: String(record?.settlementAccountCode || '').trim(),
  settlementKind: record?.settlementKind || 'payment',
  lateInterestAmount: vatLateInterestAmount(record),
  penaltyFeeAmount: vatPenaltyFeeAmount(record),
});

const vatPaymentRecordForBankGuard = (record) => ({
  amount: Math.abs(num(record?.amount)),
  date: record?.date,
  settlementAccountCode: record?.settlementAccountCode || '1920',
  settlementKind: record?.settlementKind || 'payment',
});

const assertVatPaymentMutationAllowed = (state, previousRecord, nextRecord) => {
  const previousPayment = previousRecord ? vatPaymentRecordForBankGuard(previousRecord) : null;
  const nextPayment = nextRecord ? vatPaymentRecordForBankGuard(nextRecord) : null;
  const bankChanged = previousRecord && nextRecord
    ? vatPaymentBankSignature(previousRecord) !== vatPaymentBankSignature(nextRecord)
    : Boolean(previousRecord || nextRecord);
  if (previousPayment && bankChanged) assertBankOpenForPayments(state, [previousPayment]);
  if (nextPayment) assertBankOpenForPayments(state, [nextPayment]);
};

const buildVatPaymentStatusPatch = (current, termKey, nextRecords, fallbackRecord) => {
  const basisRecord = safeArray(nextRecords)[0] || fallbackRecord || {};
  const expectedAmount = Math.abs(num(basisRecord.expectedAmount));
  const expectedKind = basisRecord.expectedSettlementKind || basisRecord.settlementKind || 'payment';
  const matchingPaidAmount = vatSettlementNetAmount(nextRecords, expectedKind);
  const fullySettled = expectedAmount > 0.005
    ? matchingPaidAmount + 0.005 >= expectedAmount
    : nextRecords.length > 0;
  if (termKey === 'mvaSubmitted') {
    return {
      ...current,
      mvaPaid: fullySettled,
      mvaPaymentRecords: { ...(current.mvaPaymentRecords || {}), [termKey]: nextRecords },
    };
  }
  return {
    ...current,
    mvaPaidTerms: { ...(current.mvaPaidTerms || {}), [termKey]: fullySettled },
    mvaPaymentRecords: { ...(current.mvaPaymentRecords || {}), [termKey]: nextRecords },
  };
};

const vatPostingAccountsForState = (state) => {
  const country = String(state?.settings?.country || 'NO').toUpperCase();
  // lateInterest/penaltyFee er bevisst utelatt for SE og DK: kontoene er ikke
  // verifisert der, og linjene droppes heller enn aa gjettes paa.
  if (country === 'SE') return { vatSettlement: '2650', company: '1930' };
  if (country === 'DK') return { vatSettlement: '6900', company: '1920' };
  return { vatSettlement: '2740', company: '1920', lateInterest: '8150', penaltyFee: '7799' };
};

const vatPaymentPostingDate = (record, year) => {
  const date = String(record?.date || '').slice(0, 10);
  const parsed = new Date(`${date}T12:00:00`);
  return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() === Number(year)
    ? date
    : `${year}-12-31`;
};

const vatPaymentYearEndLines = (state, record) => {
  const accounts = vatPostingAccountsForState(state);
  const amount = Math.abs(num(record?.amount));
  if (amount <= 0.005) return [];
  const settlementAccountCode = String(record?.settlementAccountCode || accounts.company).trim();
  if (record?.settlementKind === 'refund') {
    return [
      { accountCode: settlementAccountCode, signedAmount: amount },
      { accountCode: accounts.vatSettlement, signedAmount: -amount },
    ];
  }
  const interest = accounts.lateInterest ? vatLateInterestAmount(record) : 0;
  const fee = accounts.penaltyFee ? vatPenaltyFeeAmount(record) : 0;
  return [
    { accountCode: accounts.vatSettlement, signedAmount: amount },
    ...(interest > 0.005 ? [{ accountCode: accounts.lateInterest, signedAmount: interest }] : []),
    ...(fee > 0.005 ? [{ accountCode: accounts.penaltyFee, signedAmount: fee }] : []),
    { accountCode: settlementAccountCode, signedAmount: -(amount + interest + fee) },
  ];
};

const vatPaymentEntryId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `vat-payment-entry-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const updateVatPaymentYearEndEntries = (state, { yearKey, termKey, previousRecord, nextRecord }) => {
  const year = Number(yearKey) || new Date().getFullYear();
  const existingEntries = safeArray(state.yearEndEntriesByYear?.[yearKey]);
  const nextEntries = [...existingEntries];
  const now = nowIso();
  const previousSignature = previousRecord ? vatPaymentBankSignature(previousRecord) : '';
  const nextSignature = nextRecord ? vatPaymentBankSignature(nextRecord) : '';
  const accountingChanged = previousSignature !== nextSignature;
  const matchingActiveEntries = previousRecord
    ? existingEntries.filter((entry) =>
        entry.kind === 'vat_payment' &&
        entry.status === 'active' &&
        entry.vatTermKey === termKey &&
        entry.vatPaymentRecordId === previousRecord.id)
    : [];

  if (previousRecord && accountingChanged) {
    matchingActiveEntries.forEach((entry) => {
      const index = nextEntries.findIndex((candidate) => candidate.id === entry.id);
      if (index >= 0) nextEntries[index] = { ...nextEntries[index], status: 'reversed', reversedAt: now };
      nextEntries.push({
        id: vatPaymentEntryId(),
        batchId: entry.batchId || vatPaymentEntryId(),
        year,
        date: entry.date,
        description: `Reversering ${entry.description || 'MVA-betaling'}`,
        kind: 'reversal',
        lines: safeArray(entry.lines).map((line) => ({
          accountCode: line.accountCode,
          signedAmount: -num(line.signedAmount),
        })),
        status: 'active',
        createdAt: now,
        reversesEntryId: entry.id,
        vatTermKey: termKey,
      });
    });
  }

  const needsNewEntry = nextRecord && (accountingChanged || !matchingActiveEntries.length);
  if (needsNewEntry) {
    const lines = vatPaymentYearEndLines(state, nextRecord);
    if (lines.length) {
      nextEntries.push({
        id: vatPaymentEntryId(),
        batchId: vatPaymentEntryId(),
        year,
        date: vatPaymentPostingDate(nextRecord, year),
        description: nextRecord.settlementKind === 'refund' ? `MVA-tilbakebetaling ${year}` : `MVA-betaling ${year}`,
        kind: 'vat_payment',
        lines,
        status: 'active',
        createdAt: now,
        vatTermKey: termKey,
        vatPaymentRecordId: nextRecord.id,
      });
    }
  }

  return nextEntries;
};

const saveVatPaymentRecord = async ({ year, termKey, record }) => {
  const result = await authorizedPost('vatMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'save_payment', year, termKey, record,
  });
  applyVatMutationResult(result);
  return result.record;
};

const deleteVatPaymentRecord = async ({ year, termKey, recordId }) => {
  if (!recordId) throw new Error(t('svc.missingPaymentToCancel'));
  const result = await authorizedPost('vatMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'delete_payment', year, termKey, recordId,
  });
  applyVatMutationResult(result);
};

let statePersistQueue = Promise.resolve();
const updateStateAndPersist = async (updater) => {
  const businessId = runtime.activeBusinessId;
  const applyAndPersist = async () => {
    if (runtime.activeBusinessId !== businessId) throw new Error(t('svc.reload'));
    const previousState = runtime.appState;
    const base = mergeState(previousState);
    const next = typeof updater === 'function' ? updater(base) : updater;
    // Reject an invalid snapshot before any personnel or app-state side effect.
    const nextState = mergeState(next);
    if (hasClientServerOwnedStateChange(base, nextState)) throw new Error(t('svc.reload'));
    const changes = [];
    for (const field of ['employees', 'payrollFixedLines', 'employeePortalTravels']) {
      const before = safeArray(base[field]);
      const after = safeArray(next[field]);
      if (JSON.stringify(before) !== JSON.stringify(after)) changes.push({ field, before, after });
    }
    for (const key of new Set([...Object.keys(base.payrollSettings || {}), ...Object.keys(next.payrollSettings || {})])) {
      const before = base.payrollSettings?.[key] ?? null;
      const after = next.payrollSettings?.[key] ?? null;
      if (JSON.stringify(before) !== JSON.stringify(after)) changes.push({ field: 'payrollSettings', key, before, after });
    }
    if (changes.length) {
      const result = await authorizedPost('payrollRunMutationHttp', { businessId, action: 'update_personnel', changes });
      if (runtime.activeBusinessId === businessId) {
        // Preserve app changes received while the personnel request was running.
        runtime.appState = mergeState(PlummePersonnelState.mergePersonnelState(runtime.appState, result.state));
        emitRuntime();
      }
      return runtime.appState;
    }
    runtime.appState = nextState;
    emitRuntime();
    try {
      await persistCurrentState({ businessId, state: runtime.appState, expectedState: previousState });
      return nextState;
    } catch (error) {
      // Roll back rejected optimistic writes only if no newer update replaced them.
      if (runtime.appState === nextState) {
        runtime.appState = previousState;
        emitRuntime();
      }
      throw error;
    }
  };
  const queued = statePersistQueue.then(applyAndPersist, applyAndPersist);
  statePersistQueue = queued.catch(() => {});
  return queued;
};

const normalizeSavedInvoiceComments = (comments = []) => safeArray(comments)
  .map((entry) => {
    const text = String(entry?.text || entry || '').trim();
    if (!text) return null;
    return {
      id: String(entry?.id || `sc${Date.now()}${Math.round(Math.random() * 1e5)}`),
      // Navnet er det man plukker kommentaren fra lista med. Kommentarer lagret
      // for navnefeltet fantes har det ikke, og skal fortsatt kunne brukes —
      // lista viser da teksten i stedet.
      name: String(entry?.name || '').trim().slice(0, 60),
      text,
      createdAt: entry?.createdAt || nowIso(),
      updatedAt: entry?.updatedAt || entry?.createdAt || nowIso(),
    };
  })
  .filter(Boolean)
  .slice(0, 50);

const getSavedInvoiceComments = () => normalizeSavedInvoiceComments(mergeState(runtime.appState).settings?.savedInvoiceComments);

const saveSavedInvoiceComments = async (comments = []) => {
  const savedInvoiceComments = normalizeSavedInvoiceComments(comments);
  await updateStateAndPersist((state) => ({
    ...state,
    settings: {
      ...(state.settings || {}),
      savedInvoiceComments,
    },
  }));
  window.dispatchEvent(new Event('plumme-saved-comments-changed'));
  return savedInvoiceComments;
};

const mapCurrentUserProfile = (user, data = {}) => ({
  id: user?.uid || '',
  email: data.email || user?.email || '',
  displayName: data.displayName || user?.displayName || '',
  phoneNumber: data.phoneNumber || user?.phoneNumber || '',
  preferences: normalizeUserPreferences(data.preferences || data.uiPreferences || {}),
  createdAt: data.createdAt || '',
  updatedAt: data.updatedAt || '',
});

const getCurrentUserProfile = async () => {
  await initFirebase();
  const user = runtime.auth?.currentUser;
  if (!user) return null;
  if (!runtime.db) {
    runtime.userProfile = mapCurrentUserProfile(user);
    return runtime.userProfile;
  }
  const snapshot = await runtime.db.collection('users').doc(user.uid).get();
  const profile = mapCurrentUserProfile(user, snapshot.exists ? snapshot.data() || {} : {});
  profile.preferences = applyUserPreferences(profile.preferences);
  runtime.userProfile = profile;
  emitRuntime();
  return profile;
};

const updateCurrentUserProfile = async ({ displayName, email, phoneNumber, preferences } = {}) => {
  await initFirebase();
  const user = runtime.auth?.currentUser;
  if (!user) throw new Error(t('svc.needLoginProfile'));
  const nextName = String(displayName || '').trim();
  const nextEmail = String(email || '').trim();
  const nextPhone = String(phoneNumber || '').trim();
  const hasPreferences = preferences && typeof preferences === 'object' && !Array.isArray(preferences);
  const nextPreferences = hasPreferences ? normalizeUserPreferences(preferences) : undefined;
  if (nextName && nextName !== user.displayName && typeof user.updateProfile === 'function') {
    await user.updateProfile({ displayName: nextName });
  }
  // verifyBeforeUpdateEmail, ikke updateEmail: adressen byttes forst nar brukeren
  // har klikket lenken i den nye innboksen. Med updateEmail kunne en konto flyttes
  // til en vilkarlig ubrukt adresse og arve invitasjoner og ansattilgang knyttet
  // til den, uten a eie innboksen.
  if (nextEmail && nextEmail !== user.email) {
    if (typeof user.verifyBeforeUpdateEmail === 'function') {
      await user.verifyBeforeUpdateEmail(nextEmail);
    } else if (typeof user.updateEmail === 'function') {
      await user.updateEmail(nextEmail);
    }
  }
  if (runtime.db) {
    const profileRef = runtime.db.collection('users').doc(user.uid);
    const existing = await profileRef.get().catch(() => null);
    const timestamp = firebase?.firestore?.FieldValue?.serverTimestamp?.();
    await profileRef.set(stripUndefinedDeep({
      // Den faktiske adressen pa kontoen, ikke den ventende. Ved
      // verifyBeforeUpdateEmail byttes auth-adressen forst nar brukeren har
      // bekreftet, og profilen skal ikke pastaa noe annet i mellomtiden.
      email: runtime.auth?.currentUser?.email || user.email || '',
      displayName: nextName || user.displayName || '',
      phoneNumber: nextPhone,
      preferences: nextPreferences,
      updatedAt: timestamp || nowIso(),
      createdAt: existing?.exists ? undefined : (timestamp || nowIso()),
    }), { merge: true });
  }
  runtime.user = runtime.auth.currentUser;
  if (hasPreferences) {
    runtime.userProfile = {
      ...(runtime.userProfile || mapCurrentUserProfile(runtime.user)),
      preferences: applyUserPreferences(nextPreferences),
    };
  }
  emitRuntime();
  return getCurrentUserProfile();
};

// Oppdater ÉN preferanse uten å røre de andre.
// updateCurrentUserProfile erstatter hele preferences-objektet i minnet, så en
// delvis payload derfra ville tømt locale/mascots/layout til neste Firestore-lesing.
// Her slås patchen sammen med gjeldende preferanser først.
const updateUserPreferencePatch = async (patch = {}) => {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return null;
  const current = runtime.userProfile?.preferences || (await getCurrentUserProfile())?.preferences || {};
  const merged = { ...current, ...patch };
  if (patch.notifications || current.notifications) {
    merged.notifications = { ...(current.notifications || {}), ...(patch.notifications || {}) };
  }
  return updateCurrentUserProfile({ preferences: merged });
};
